import React from 'react';
import { Activity, Calendar, Target } from 'lucide-react';
import { usePracticeSession } from '@/hooks/usePracticeSession';
import { Player } from '@/types/player';

interface PlayerDashboardProps {
  player: Player;
  teamId: string;
}

interface ArchKPIProps {
  icon: React.ReactNode;
  title: string;
  value: string | number;
  subtitle: string;
  progress: number;
  className: string;
}

const ArchKPI = ({ icon, title, value, subtitle, progress, className }: ArchKPIProps) => (
  <div className={`arch-container ${className} p-3 sm:p-4 relative`}>
    <div className="flex items-center gap-2 sm:gap-3 mb-2">
      <div className="arch-icon flex-shrink-0 text-white">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-xs sm:text-sm text-muted-foreground">{title}</h3>
        <div className="text-lg sm:text-xl font-bold" style={{ color: 'var(--accent-color)' }}>
          {value}
        </div>
      </div>
    </div>
    <p className="text-xs text-muted-foreground truncate mb-2">{subtitle}</p>
    <div 
      className="arch-progress" 
      style={{ transform: `scaleX(${progress / 100})` }}
    />
  </div>
);

export const PlayerDashboard = ({ player, teamId }: PlayerDashboardProps) => {
  const { sessions, loading } = usePracticeSession(teamId);

  // Calculate Activities KPIs
  const totalActivities = 12; // This would come from actual activities data
  const favActivity = "Drills";
  const trainingHours = 24;

  // Calculate Assignments (Practice Sessions) KPIs
  const totalSessions = sessions.length;
  const attendedSessions = Math.floor(totalSessions * 0.85); // 85% attendance mock
  const attendanceRate = totalSessions > 0 ? Math.round((attendedSessions / totalSessions) * 100) : 0;
  const nextSession = sessions.find(s => s.status === 'scheduled');

  // Calculate Assessments KPIs
  const skillImprovement = 15; // Mock percentage improvement
  const currentRating = 7.8; // Mock performance rating
  const achievementCount = 3; // Mock achievement badges

  if (loading) {
    return (
      <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="arch-container arch-activities p-3 sm:p-6 animate-pulse">
            <div className="h-8 sm:h-12 w-8 sm:w-12 bg-muted rounded-full mx-auto mb-2 sm:mb-3" />
            <div className="h-3 sm:h-4 bg-muted rounded mb-1 sm:mb-2" />
            <div className="h-4 sm:h-6 bg-muted rounded mb-1" />
            <div className="h-2 sm:h-3 bg-muted rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div></div>
  );
};